import { API_URLS, getApiUrl } from '../Utils/apiURLConfiguration';
import GWRApiClient from './GWRApiClient';

const kidsQueryString = availabilityRequest => {
  if (!availabilityRequest?.kidsAges) return '';
  let kidsQs = '';
  if (typeof availabilityRequest.kidsAges === 'object') {
    availabilityRequest.kidsAges.forEach(kidAge => {
      if (kidAge !== '-') {
        kidsQs += '&kidsAges=' + kidAge;
      }
    });
  } else {
    availabilityRequest.kidsAges.split(',').forEach(kidAge => {
      if (kidAge !== '-') {
        kidsQs += '&kidsAges=' + kidAge;
      }
    });
  }
  return kidsQs;
};

/**
 * Concrete implementation for the RoomBooking endpoints
 *
 * @class RoomBookingApiclient
 * @extends {GWRApiClient}
 */
export default class RoomBookingApiClient extends GWRApiClient {
  constructor(version = '', token = '') {
    let apiVersion = version !== '' ? version : `v1`;
    const urlAux = getApiUrl(API_URLS.REACT_APP_ROOM_BOOKING_ENDPOINT, apiVersion);
    super(urlAux, token);
  }

  /**
   * @function
   * Create a new reservation for the current user using the new Tioga's API
   * @param {object} bookingRequest
   */
  async createSuiteReservation(bookingRequest) {
    const urlRequest = `${this.url}/${bookingRequest.lodge.code}/book`;
    return await super.postApiCallAuth(urlRequest, bookingRequest);
  }

  /**
   * function returns a cart built outside the funnel, e.g. by the chat agent
   *
   * @function
   * @param {string} lodgeCode
   * Example: 'CONCNC'
   * @param {string} cartId
   * Example: 'a8126086-c1b1-4ab4-88d5-33e0c3a95315'
   */
  async getCart(lodgeCode, cartId) {
    const urlRequest = `${this.url}/${lodgeCode}/carts/${cartId}`;
    return await super.getApiCallAuth(urlRequest);
  }

  /**
   * function returns the highest suite rates by location for a given date range
   *
   * @function
   * @param {arrival: date, departure: date, numberOfAdults: string, kidsAges: Array[]} availabilityHighestRatesRequest
   * Example: {arrival: '2019-06-01', departure: '2019-06-03', numberOfAdults: 1, kidsAges: [8,6]
   * @param {string} location
   */

  async fetchAvailabilityHighestRates(availabilityHighestRatesRequest, location) {
    if (!location) console.error('No location passed in, location is needed to make /highest-rates call');

    const urlRequest = `${this.url}/${location}/highest-rates`;

    let request = await super.getApiCallAuth(urlRequest, availabilityHighestRatesRequest);
    return request;
  }

  /**
   * function returns the lowest rates for calendar
   *
   * @function
   * @param {Year: int, Month: int, Adults: int, KidsAges: Array[int], OfferCode: string}
   * @param {string} location
   * Example: getLowestRates({lodgeCode: GURNIL, Year: 2024, Month: 5, Adults: 1, KidsAges: [14,5,15,2], OfferCode: AMTCERT}, 'GURNIL')
   */
  async getLowestRates(lowestRatesRequest, location) {
    const queryString =
      `${this.url}/${location}/lowest-rates` +
      `?year=${lowestRatesRequest.year}` +
      `&month=${lowestRatesRequest.month}` +
      `&numberOfMonths=${lowestRatesRequest.numberOfMonths}` +
      `&adults=${lowestRatesRequest.adults}` +
      `&offerCode=${lowestRatesRequest.offerCode}` +
      `${kidsQueryString(lowestRatesRequest)}`;

    let request = await super.getApiCallAuth(queryString);
    return request;
  }

  /**
   * function to get all the range of days of availability data by suite
   *
   * @function
   * @param {string} suiteCode
   * The suite which we are requesting
   * Example: 'WPFB'
   * @param {numberOfAdults: int, location: string, arrival: date, departure: date, offerCode: string, numNights: int, kidsAges: string} availabilityRangeBySuiteRequest
   * Example: {numberOfAdults: 2, location: 'DELLWI', arrival: '2019-06-01', departure: '2019-06-03', offerCode: 'FLEXTEST', numNights: 2, kidsAges: '0,1,2,3'}
   */
  async getAvailabilityRangeBySuite(suiteCode, availabilityRequest) {
    const queryString =
      `?offerCode=${availabilityRequest.offerCode}` +
      `&numberOfNights=${availabilityRequest.numberOfNights || availabilityRequest.numNights}` +
      `&adults=${availabilityRequest.adults || availabilityRequest.numberOfAdults}` +
      `&arrival=${availabilityRequest.arrival}` +
      `&departure=${availabilityRequest.departure}` +
      `${kidsQueryString(availabilityRequest)}`;

    const urlRequest =
      `${this.url}/${availabilityRequest.lodgeCode || availabilityRequest.location}/suites/${suiteCode}/rate-calendar` +
      queryString;

    let request = await super.getApiCallAuth(urlRequest);
    return request;
  }

  /**
   * function returns the lowest rates for calendar
   *
   * @function
   * @param {year: number, month: number, adults: number, numberOfMonths: number, kidsAges: Array[number], offerCode: string}
   * @param {string} suiteCode
   * @param {string} location
   */
  async getLowestRatesForSuite(lowestRatesRequest, suiteCode, location) {
    const queryString =
      `${this.url}/${location}/suites/${suiteCode}/lowest-rates` +
      `?lodgeCode=${lowestRatesRequest.lodgeCode}` +
      `&year=${lowestRatesRequest.year}` +
      `&month=${lowestRatesRequest.month}` +
      `&numberOfMonths=${lowestRatesRequest.numberOfMonths}` +
      `&adults=${lowestRatesRequest.adults}` +
      `&offerCode=${lowestRatesRequest.offerCode}` +
      `${kidsQueryString(lowestRatesRequest)}`;

    return await super.getApiCallAuth(queryString);
  }

  /**
   * function returns the lowest rates by stay without suiteCode
   *
   * @function
   * @param {lodgeCode: string, arrival: date, departure: date, adults: number, kidsAges: Array[number], location: string} lowestRatesByStayRequest
   */
  async getLowestRatesForSuiteByStayNoSuiteCode(lowestRatesByStayRequest) {
    const queryString = `${this.url}/${lowestRatesByStayRequest.lodgeCode}/lowest-rates/by-stay?${kidsQueryString(
      lowestRatesByStayRequest
    )}`;
    delete lowestRatesByStayRequest.kidsAges;
    return await super.getApiCallAuth(queryString, lowestRatesByStayRequest);
  }

  /**
   * function returns the lowest rates by stay
   *
   * @function
   * @param {lodgeCode: string, arrival: date, departure: date, adults: number, kidsAges: Array[number], offerCode: string} lowestRatesByStayRequest
   * @param {string} suiteCode
   * @param {string} location
   */
  async getLowestRatesForSuiteByStay(lowestRatesByStayRequest, suiteCode, location) {
    const queryString = `${this.url}/${location}/suites/${suiteCode}/lowest-rates/by-stay?${kidsQueryString(
      lowestRatesByStayRequest
    )}`;
    delete lowestRatesByStayRequest.kidsAges;

    return await super.getApiCallAuth(queryString, lowestRatesByStayRequest);
  }

  /**
   * @function
   * Sends a POST request to get an access block code
   * @param property
   */
  async getShift4i4AccessBlockTioga(property, paymentType, totalToCharge, origin = null) {
    const urlRequest = `${this.url}/${property}/payment/token`;
    const payload = {
      orderDetails: {
        amount: totalToCharge, // Total paid
        paymentType: paymentType // payment type selected by the user
      },
      ...(typeof origin === 'number' && { origin })
    };
    return super.postApiCallAuth(urlRequest, payload);
  }

  /**
   * @function
   * Sends a GET request to get the recommendation to show the modal
   * @param property
   */
  async getAddOnsRecommendations(property, payload) {
    const urlRequest = `${this.url}/${property}/add-ons/recommendation`;
    return await super.getApiCallAuth(urlRequest, payload);
  }

  /**
   * function to get all the availability data
   *
   * @function
   * @param {numberOfAdults: int, location: string, arrival: date, departure: date, offerCode: string, kidsAges= string} availabilityRequest
   * Example: {numberOfAdults: 2, location: 'DELLWI', arrival: '2019-06-01', departure: '2019-06-03', offerCode: 'FLEXTEST', kidsAges: '0,1,2,3,4'}
   */
  async fetchAvailability(availabilityRequest) {
    const requiredFields = ['numberOfAdults', 'location', 'arrival', 'departure'];
    const urlRequest = `${this.url}/${availabilityRequest.location}/availability`;
    return await super.getApiCallAuth(urlRequest, availabilityRequest, requiredFields);
  }

  /**p
   * function to get all the availability data by suite
   *
   * @function
   * @param {string} suite
   * The suite which we are requesting
   * Example: 'WPFB'
   * @param {numberOfAdults: int, location: string, arrival: date, departure: date, offerCode: string, rateCode: string, kidsAges: string} availabilityBySuiteRequest
   * Example: {numberOfAdults: 2, location: 'DELLWI', arrival: '2019-06-01', departure: '2019-06-03', offerCode: 'FLEXTEST', rateCode:'BBAR', kidsAges: '0,1,2,3'}
   */
  async getAvailabilityBySuite(lodgeCode, suite, availabilityBySuiteRequest) {
    const urlRequest = `${this.url}/${lodgeCode}/suites/${suite}/availability`;
    return await super.getApiCallAuth(urlRequest, availabilityBySuiteRequest);
  }

  /**
   * function returns the promo banner information for a given lodge and stay
   *
   * @function
   * @param {string} lodgeCode
   * The lodge which we are requesting
   * Example: 'DELLWI'
   * @param {numberOfAdults: number, arrival: date, departure: date, kidsAges: Array[number], offerCode: string} promoBannerRequest
   * Example: {numberOfAdults: 2, arrival: '2019-06-01', departure: '2019-06-03', kidsAges: [8,6], offerCode: 'FLEXTEST'}
   * @param {AbortSignal} [signal]
   * Optional AbortController signal so the caller can cancel the request (e.g. on timeout).
   */
  async getPromoBanner(lodgeCode, promoBannerRequest, signal) {
    const urlRequest = `${this.url}/${lodgeCode}/promo-banner`;
    const params = {
      numberOfAdults: promoBannerRequest.numberOfAdults,
      arrival: promoBannerRequest.arrival,
      departure: promoBannerRequest.departure,
      kidsAges: promoBannerRequest.kidsAges,
      OfferCode: promoBannerRequest.offerCode
    };
    return await super.getApiCallAuth(urlRequest, params, [], true, false, signal);
  }
}
